import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { Card, Btn, Input, PageHeader } from '../components/UI';
import { adminApi } from '../utils/api';

const RED = '#C5161D';

const Toggle = ({ value, onChange, label, desc }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #F1F5F9' }}>
    <div>
      <div style={{ color: '#0F172A', fontSize: 13, fontWeight: 600 }}>{label}</div>
      {desc && <div style={{ color: '#94A3B8', fontSize: 11, marginTop: 2 }}>{desc}</div>}
    </div>
    <button type="button" onClick={() => onChange(!value)}
      style={{ width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', background: value ? '#EF1D28' : '#CBD5E1', flexShrink: 0 }}>
      <div style={{ position: 'absolute', top: 3, left: value ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: 'white', transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} />
    </button>
  </div>
);

const NumField = ({ label, desc, value, onChange, min, prefix, suffix }) => (
  <div style={{ padding: '12px 0', borderBottom: '1px solid #F1F5F9' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div>
        <div style={{ color: '#0F172A', fontSize: 13, fontWeight: 600 }}>{label}</div>
        {desc && <div style={{ color: '#94A3B8', fontSize: 11, marginTop: 2 }}>{desc}</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        {prefix && <span style={{ color: '#64748B', fontSize: 13 }}>{prefix}</span>}
        <input type="number" value={value} min={min ?? 0} onChange={e => onChange(Number(e.target.value))}
          style={{ width: 72, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 7, padding: '6px 10px', fontSize: 13, color: '#0F172A', outline: 'none', textAlign: 'center' }} />
        {suffix && <span style={{ color: '#64748B', fontSize: 13 }}>{suffix}</span>}
      </div>
    </div>
  </div>
);

export const SettingsPage = () => {
  const { admin } = useAdminAuth();

  const [pwForm,   setPwForm]  = useState({ current: '', newPw: '', confirm: '' });
  const [pwMsg,    setPwMsg]   = useState({ text: '', ok: false });
  const [pwSaving, setPwSaving]= useState(false);

  const [cfg,      setCfg]     = useState(null);
  const [cfgSaving,setCfgSaving]=useState(false);
  const [cfgMsg,   setCfgMsg]  = useState('');

  useEffect(() => {
    adminApi.getPlatformConfig().then(setCfg).catch(() => {});
  }, []);

  const changePassword = async () => {
    setPwMsg({ text: '', ok: false });
    if (!pwForm.current || !pwForm.newPw || !pwForm.confirm) return setPwMsg({ text: 'All fields required', ok: false });
    if (pwForm.newPw.length < 8) return setPwMsg({ text: 'Password must be at least 8 characters', ok: false });
    if (pwForm.newPw !== pwForm.confirm) return setPwMsg({ text: 'New passwords do not match', ok: false });
    setPwSaving(true);
    try {
      await adminApi.login(admin.email, pwForm.current);
      await fetch('/api/admin/users/' + admin._id, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
        body: JSON.stringify({ password: pwForm.newPw }),
      });
      setPwMsg({ text: '✅ Password changed successfully', ok: true });
      setPwForm({ current: '', newPw: '', confirm: '' });
    } catch (e) {
      setPwMsg({ text: e.message.includes('Invalid') ? 'Current password is incorrect' : e.message, ok: false });
    } finally { setPwSaving(false); }
  };

  const saveCfg = async () => {
    setCfgSaving(true); setCfgMsg('');
    try {
      await adminApi.updatePlatformConfig(cfg);
      setCfgMsg('✅ Platform settings saved successfully');
    } catch (e) { setCfgMsg('❌ ' + e.message); }
    finally { setCfgSaving(false); setTimeout(() => setCfgMsg(''), 4000); }
  };

  const set = (key, val) => setCfg(p => ({ ...p, [key]: val }));

  const infoRow = (label, value) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #F1F5F9' }}>
      <span style={{ color: '#64748B', fontSize: 13 }}>{label}</span>
      <span style={{ color: '#0F172A', fontSize: 13, fontWeight: 600 }}>{value}</span>
    </div>
  );

  return (
    <div>
      <PageHeader title="Settings" subtitle="Admin account, platform configuration and feature flags" />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Account Info */}
        <Card>
          <div style={{ color: '#0F172A', fontWeight: 700, marginBottom: 16, fontSize: 15 }}>👤 Account Information</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16, padding: 14, background: '#F8FAFC', borderRadius: 10 }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'linear-gradient(135deg,#EF1D28,#FB923C)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 800, fontSize: 20 }}>
              {admin?.name?.charAt(0)}
            </div>
            <div>
              <div style={{ color: '#0F172A', fontWeight: 700, fontSize: 16 }}>{admin?.name}</div>
              <div style={{ color: '#64748B', fontSize: 13 }}>{admin?.email}</div>
              <span style={{ background: admin?.role === 'superadmin' ? '#F3E8FF' : '#FFF7ED', color: admin?.role === 'superadmin' ? '#7E22CE' : '#FB923C', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
                {admin?.role?.toUpperCase()}
              </span>
            </div>
          </div>
          {infoRow('Name', admin?.name)}
          {infoRow('Email', admin?.email)}
          {infoRow('Role', admin?.role)}
          {infoRow('Status', 'Active')}
        </Card>

        {/* Change Password */}
        <Card>
          <div style={{ color: '#0F172A', fontWeight: 700, marginBottom: 16, fontSize: 15 }}>🔐 Change Password</div>
          {pwMsg.text && (
            <div style={{ background: pwMsg.ok ? '#DCFCE7' : '#FEF2F2', border: `1px solid ${pwMsg.ok ? '#BBF7D0' : '#FECACA'}`, color: pwMsg.ok ? '#15803D' : '#DC2626', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
              {pwMsg.text}
            </div>
          )}
          <Input label="Current Password" type="password" value={pwForm.current} onChange={v => setPwForm(p => ({ ...p, current: v }))} placeholder="Enter current password" />
          <Input label="New Password"     type="password" value={pwForm.newPw}   onChange={v => setPwForm(p => ({ ...p, newPw: v }))}   placeholder="Min 8 characters" />
          <Input label="Confirm Password" type="password" value={pwForm.confirm} onChange={v => setPwForm(p => ({ ...p, confirm: v }))} placeholder="Repeat new password" />
          <Btn color="blue" onClick={changePassword} disabled={pwSaving} style={{ width: '100%' }}>
            {pwSaving ? 'Updating…' : 'Update Password'}
          </Btn>
        </Card>
      </div>

      {/* Platform Config */}
      {cfg && (
        <Card style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <div>
              <div style={{ color: '#0F172A', fontWeight: 700, fontSize: 15 }}>⚙️ Platform Configuration</div>
              <div style={{ color: '#94A3B8', fontSize: 12, marginTop: 2 }}>Controls features and limits shown to all users in the client dashboard</div>
            </div>
            <Btn color="red" onClick={saveCfg} disabled={cfgSaving}>{cfgSaving ? 'Saving…' : 'Save Changes'}</Btn>
          </div>
          {cfgMsg && <div style={{ background: cfgMsg.startsWith('✅') ? '#DCFCE7' : '#FEF2F2', border: `1px solid ${cfgMsg.startsWith('✅') ? '#BBF7D0' : '#FECACA'}`, color: cfgMsg.startsWith('✅') ? '#15803D' : '#DC2626', padding: '9px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{cfgMsg}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {/* Plan limits */}
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#475569', marginBottom: 4, paddingBottom: 8, borderBottom: '2px solid #F1F5F9' }}>💳 Plan Limits</div>
              <NumField label="Free Plan Document Limit" desc="Max documents a Free user can create" value={cfg.freeDocLimit} onChange={v => set('freeDocLimit', v)} min={1} suffix="docs" />
              <NumField label="Pro Weekly Price" desc="Weekly billing price displayed to users (USD)" value={cfg.proWeeklyPrice ?? 8} onChange={v => set('proWeeklyPrice', v)} min={1} prefix="$" suffix="/wk" />
              <NumField label="Pro Monthly Price" desc="Monthly billing price displayed to users (USD)" value={cfg.proMonthlyPrice ?? 20} onChange={v => set('proMonthlyPrice', v)} min={1} prefix="$" suffix="/mo" />
              <NumField label="Referral Bonus Days" desc="Free Pro days awarded to referred new users" value={cfg.referralBonusDays} onChange={v => set('referralBonusDays', v)} min={0} suffix="days" />
            </div>

            {/* Feature flags */}
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#475569', marginBottom: 4, paddingBottom: 8, borderBottom: '2px solid #F1F5F9' }}>🚦 Feature Flags</div>
              <Toggle label="AI Features on Free Plan" desc="Allow Free users to access AI writing tools" value={cfg.aiOnFree} onChange={v => set('aiOnFree', v)} />
              <Toggle label="Downloads on Free Plan" desc="Allow Free users to download PDF/Word documents" value={cfg.downloadOnFree} onChange={v => set('downloadOnFree', v)} />
              <Toggle label="Job Search" desc="Enable the Job Search section in user dashboard" value={cfg.jobSearchEnabled} onChange={v => set('jobSearchEnabled', v)} />
              <Toggle label="Interview Prep" desc="Enable the Interview Prep section in user dashboard" value={cfg.interviewPrepEnabled} onChange={v => set('interviewPrepEnabled', v)} />
              <Toggle label="New User Signups" desc="Allow new users to register (disable during maintenance)" value={cfg.signupsEnabled} onChange={v => set('signupsEnabled', v)} />
              <Toggle label="Maintenance Mode" desc="Show maintenance banner to all users" value={cfg.maintenanceMode} onChange={v => set('maintenanceMode', v)} />
            </div>
          </div>
        </Card>
      )}

      {/* Sidebar Navigation Visibility */}
      {cfg && (
        <Card style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <div>
              <div style={{ color: '#0F172A', fontWeight: 700, fontSize: 15 }}>🧭 Sidebar Navigation</div>
              <div style={{ color: '#94A3B8', fontSize: 12, marginTop: 2 }}>Show or hide items in the user-facing sidebar. Hidden items are completely removed for all users.</div>
            </div>
            <Btn color="red" onClick={saveCfg} disabled={cfgSaving}>{cfgSaving ? 'Saving…' : 'Save Changes'}</Btn>
          </div>
          {cfgMsg && <div style={{ background: cfgMsg.startsWith('✅') ? '#DCFCE7' : '#FEF2F2', border: `1px solid ${cfgMsg.startsWith('✅') ? '#BBF7D0' : '#FECACA'}`, color: cfgMsg.startsWith('✅') ? '#15803D' : '#DC2626', padding: '9px 14px', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{cfgMsg}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
            {[
              { id: 'dashboard',    label: 'Dashboard',    desc: 'Main dashboard / home view' },
              { id: 'interviews',   label: 'Job Interview', desc: 'Interview prep section (Pro)' },
              { id: 'job_search',   label: 'Job Search',   desc: 'Job search section (Pro)' },
              { id: 'templates',    label: 'Templates',    desc: 'Template gallery browser' },
              { id: 'resume',       label: 'My Documents', desc: 'Resumes, CVs, cover letters' },
              { id: 'subscription', label: 'My Plan',      desc: 'Billing and subscription page' },
              { id: 'referral',     label: 'Refer & Earn', desc: 'Referral rewards program' },
            ].map(item => {
              const hidden = (cfg.hiddenNavItems || []).includes(item.id);
              const toggleNav = () => {
                const current = cfg.hiddenNavItems || [];
                const next = hidden ? current.filter(x => x !== item.id) : [...current, item.id];
                set('hiddenNavItems', next);
              };
              return (
                <Toggle key={item.id} label={item.label} desc={item.desc} value={!hidden} onChange={toggleNav} />
              );
            })}
          </div>
        </Card>
      )}

      {/* Platform Links */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <Card>
          <div style={{ color: '#0F172A', fontWeight: 700, marginBottom: 16, fontSize: 15 }}>🌐 Platform Links</div>
          {[
            ['Main App',      process.env.REACT_APP_CLIENT_URL || 'https://marsresume.com',  '#15803D'],
            ['Admin Panel',   `${window.location.origin}/admin`,                             '#FB923C'],
            ['API Server',    `${window.location.origin}/api/health`,                        '#7E22CE'],
            ['MongoDB Atlas', 'https://cloud.mongodb.com',                                   '#B45309'],
          ].map(([label, url, color]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F1F5F9' }}>
              <span style={{ color: '#64748B', fontSize: 13 }}>{label}</span>
              <a href={url} target="_blank" rel="noreferrer" style={{ color, fontSize: 12, textDecoration: 'none', fontWeight: 600 }}>{url} ↗</a>
            </div>
          ))}
        </Card>

        <Card>
          <div style={{ color: '#0F172A', fontWeight: 700, marginBottom: 16, fontSize: 15 }}>🔧 Integration Status</div>
          {[
            ['MongoDB',   'Connected',          '#15803D', '#DCFCE7'],
            ['Stripe',    'Configure .env key', '#D97706', '#FEF3C7'],
            ['Anthropic', 'AI features active', '#15803D', '#DCFCE7'],
            ['Google OAuth', 'OAuth active',    '#15803D', '#DCFCE7'],
            ['LinkedIn OAuth','OAuth active',   '#15803D', '#DCFCE7'],
          ].map(([label, status, color, bg]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #F1F5F9' }}>
              <span style={{ color: '#334155', fontSize: 13, fontWeight: 500 }}>{label}</span>
              <span style={{ background: bg, color, fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 20 }}>{status}</span>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
};
